'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion'
import { cn } from '@/utilities/cn'
import { Truck, Gift } from 'lucide-react'

export interface ShippingInfo {
  shippingCost: number
  isFree: boolean
  remainingForFreeShipping: number
  country?: string
  freeShippingThreshold: number
}

export interface ShippingCalculatorProps {
  shippingInfo: ShippingInfo | null
  cartTotal: number
  className?: string
  showProgressBar?: boolean
  containsManualDeliveryItems?: boolean
  isPickup?: boolean
}

export const ShippingCalculator: React.FC<ShippingCalculatorProps> = ({
  shippingInfo,
  cartTotal,
  className,
  showProgressBar = true,
  containsManualDeliveryItems = false,
  isPickup = false,
}) => {
  const t = useTranslations('shipping')
  const [justCompleted, setJustCompleted] = useState(false)

  // Calculate progress values
  const {
    remainingForFreeShipping = 0,
    isFree = false,
    freeShippingThreshold = 10000,
  } = shippingInfo || {}

  const progressPercentage = useMemo(() => {
    if (isFree) return 100
    if (freeShippingThreshold <= 0) return 0
    return Math.min(100, (cartTotal / freeShippingThreshold) * 100)
  }, [isFree, cartTotal, freeShippingThreshold])

  const isComplete = isFree || progressPercentage >= 100

  // Spring animation for smooth progress
  const progressSpring = useSpring(progressPercentage, {
    stiffness: 100,
    damping: 20,
  })

  // Glow intensity for completed state
  const glowOpacity = useTransform(progressSpring, [80, 100], [0, 1])

  // Trigger celebration effect
  useEffect(() => {
    if (isComplete && !justCompleted && shippingInfo) {
      const timer = setTimeout(() => {
        setJustCompleted(true)
        setTimeout(() => setJustCompleted(false), 3000)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isComplete, justCompleted, shippingInfo])

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount / 100) // Convert cents to euros
  }

  // Don't render if manual delivery items are present
  if (containsManualDeliveryItems || !shippingInfo) {
    return null
  }

  if (!showProgressBar) {
    return null
  }

  // Pickup in Store — show simple "free" message instead of progress bar
  if (isPickup) {
    return (
      <div
        className={cn(
          'relative rounded-lg border border-border/50 bg-neutral-100 dark:bg-black p-4',
          className,
        )}
      >
        <div className="flex items-center gap-2">
          <Gift className="w-4 h-4 text-[var(--neon-orange)]" />
          <span className="text-xs font-mono uppercase tracking-widest text-neutral-800 dark:text-neutral-200">
            {t('title')}
          </span>
        </div>
        <p className="text-sm font-semibold text-foreground mt-2 flex items-center gap-2">
          <span className="text-lg">🏪</span>
          {t('pickupFree')}
        </p>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'relative rounded-lg border border-border/50 bg-neutral-100 dark:bg-black p-4',
        className,
      )}
    >
      {/* Header with icon */}
      <div className="flex items-center gap-2 mb-3">
        <motion.div
          animate={isComplete ? { scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] } : {}}
          transition={{ duration: 0.5, repeat: isComplete ? Infinity : 0, repeatDelay: 2 }}
        >
          {isComplete ? (
            <Gift className="w-4 h-4 text-[var(--neon-orange)]" />
          ) : (
            <Truck className="w-4 h-4 text-neutral-800 dark:text-neutral-200" />
          )}
        </motion.div>
        <span className="text-xs font-mono uppercase tracking-widest text-neutral-800 dark:text-neutral-200">
          {t('title')}
        </span>
      </div>

      {/* Progress text */}
      <div className="mb-3">
        <AnimatePresence mode="wait">
          <motion.p
            key={isComplete ? 'complete' : 'progress'}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={cn(
              'text-sm font-medium',
              isComplete ? 'text-foreground font-semibold' : 'text-foreground',
            )}
          >
            {isComplete ? (
              <span className="flex items-center gap-2 font-semibold">
                {t('freeShippingApplied')}
              </span>
            ) : (
              t('freeShippingProgress', {
                remaining: formatCurrency(remainingForFreeShipping),
              })
            )}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Progress bar */}
      <div className="relative h-2 bg-muted rounded-full overflow-hidden">
        {/* Background track */}
        <div className="absolute inset-0 bg-gradient-to-r from-muted to-muted/80" />

        {/* Progress fill */}
        <motion.div
          className={cn(
            'absolute inset-y-0 left-0 rounded-full',
            isComplete
              ? 'bg-gradient-to-r from-[var(--neon-orange)] via-orange-400 to-[var(--neon-orange)]'
              : 'bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500',
          )}
          style={{ width: `${progressPercentage}%` }}
          initial={false}
          animate={{
            boxShadow: isComplete
              ? [
                  '0 0 10px rgba(255, 140, 0, 0.3)',
                  '0 0 20px rgba(255, 140, 0, 0.5)',
                  '0 0 10px rgba(255, 140, 0, 0.3)',
                ]
              : '0 0 0px transparent',
          }}
          transition={{
            boxShadow: {
              duration: 1.5,
              repeat: isComplete ? Infinity : 0,
              repeatType: 'reverse',
            },
          }}
        >
          {/* Shimmer effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            animate={{ x: ['-100%', '200%'] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
              repeatDelay: 1,
            }}
          />
        </motion.div>
      </div>

      {/* Progress percentage */}
      <div className="flex justify-between items-center mt-2">
        <span className="text-[10px] font-mono text-neutral-600 dark:text-neutral-400 uppercase tracking-wider">
          {Math.round(progressPercentage)}%
        </span>
        <span className="text-[10px] font-mono text-neutral-600 dark:text-neutral-400">
          {isComplete
            ? t('freeShippingUnlocked')
            : `${formatCurrency(freeShippingThreshold)} ${t('threshold')}`}
        </span>
      </div>

      {/* Shipping cost display when not free */}
      {!isComplete && shippingInfo.shippingCost > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-3 pt-3 border-t border-border/50"
        >
          <div className="flex justify-between items-center">
            <span className="text-xs text-neutral-600 dark:text-neutral-400">
              {t('currentShipping')}
            </span>
            <span className="text-sm font-mono font-medium text-black dark:text-white">
              {formatCurrency(shippingInfo.shippingCost)}
            </span>
          </div>
        </motion.div>
      )}
    </div>
  )
}

export default ShippingCalculator
